// src/components/GpsUnitList.js
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../../components/AdminHeader';
import LayoutContainer from '../../components/LayoutContainer';
import { filter, flatMap, groupBy, includes, map } from 'lodash';
import MUIDataTable from 'mui-datatables';
import itemService from '../../services/itemService';
import { toast } from 'react-toastify';
import { Value } from 'sass';
import DeleteModal from '../../components/DeleteModal';

const GpsUnitList = () => {
  const [units, setUnits] = useState([]);
  const navigate = useNavigate();
  const roles = localStorage?.getItem('roles');
  const permissions = localStorage?.getItem('permissions');
  const isCMCPermission = permissions?.includes('COMMAND CENTER');
  const isCMCRoles = roles?.includes('COMMAND CENTER');
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [rowsToDelete, setRowsToDelete] = useState(null);

  const fetchUnits = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/gps_unit`
    );
    setUnits(groupBy(response?.data, 'unit_type'));
  };

  useEffect(() => {
    fetchUnits();
  }, []);

  // const handleDelete = async (id) => {
  //   if (window.confirm('Are you sure you want to delete this GPS unit?')) {
  //     await axios.delete(
  //       `${process.env.REACT_APP_API_URL}/gps_unit/${id}`
  //     );
  //     setUnits(units.filter((unit) => unit.id !== id));
  //   }
  // };

  const handleDelete = async (data) => {
    const deleteItemIndex = map(data?.data, (data) => data?.dataIndex);
    const deleteItem = filter(
      flatMap(units, (units) => units),
      (data, index) => includes(deleteItemIndex, index)
    );

    try {
      const res = await itemService.deleteProductList({
        table_name: 'gps_unit',
        ids: map(deleteItem, 'id'),
      });

      if (res?.status === 204) {
        toast?.success('GPS Unit removed successfully');
        // setUnits(
        //   flatMap(units, (units) => units)?.filter((item, index) => !includes(deleteItemIndex, index))
        // );
        setOpenDeleteModal(false);
        fetchUnits();
      }
    } catch (error) {
      toast?.error(error?.response?.data?.message);
    }
  };

  const columns = [
    {
      name: 'id',
      label: 'Id',
      options: {
        filter: false,
        sort: true,
        display: false,
      },
    },
    {
      name: 'icon',
      label: 'ICON',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => {
          return (
            <img
              src={`${process.env.PUBLIC_URL}/icons/${value}`}
              alt='icon'
              style={{
                display: 'flex',
                height: value === 'ambulance.svg' ? '30px' : '30px',
                width: value === 'ambulance.svg' && '30px',
                // marginRight: value === 'ambulance.svg' ? '10px' : '10px',
              }}
            />
          );
        },
      },
    },
    {
      name: 'name',
      label: 'UNIT NAME',
      customBodyRender: (value) => (value ? value.toUpperCase() : '-'),
    },
    {
      name: 'unit_type',
      label: 'UNIT TYPE',
      customBodyRender: (value) => (value ? value.toUpperCase() : '-'),
    },
    {
      name: 'user_name',
      label: 'USER NAME',
      options: {
        customBodyRender: (value) => (value ? value.toUpperCase() : '-'),
      },
    },
    {
      name: 'actions',
      label: 'ACTION',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta, updateValue) => {
          const unit = units[tableMeta.rowIndex];
          return (
            <>
              <IconButton
                onClick={() => navigate(`/gpsUnitForm/${tableMeta.rowData[0]}`)}
              >
                <EditIcon sx={{ color: '#be210b' }} />
              </IconButton>
              {/* <IconButton onClick={() => handleDelete(unit?.id)}>
                <DeleteIcon sx={{ color: '#be210b' }} />
              </IconButton> */}
            </>
          );
        },
      },
    },
  ];

  const data = Object?.keys(units)?.reduce((acc, unitType) => {
    const unitData =
      units[unitType] &&
      units[unitType]?.map((unit) => ({
        icon: unit.icon,
        name: unit.name,
        unit_type: unit.unit_type,
        user_name: unit.user_name || '-',
        id: unit.id,
      }));
    return [...acc, ...unitData];
  }, []);

  const options = {
    expandableRows: false,
    download: false,
    filterOptions: 'fullWidth',
    selectableRows: 'multiple',
    print: false,
    onRowsDelete: (rowsDeleted) => {
      setRowsToDelete(rowsDeleted);
      setOpenDeleteModal(true);
      return false;
    },
    // onRowsDelete: (rowsDeleted) => {
    //   const confirmDelete = window.confirm(
    //     `Are you sure you want to delete ${rowsDeleted.data.length} row(s)?`
    //   );
    //   if (confirmDelete) {
    //     handleDelete(rowsDeleted);
    //   } else {
    //     return false;
    //   }
    // },
    print: false,
    rowsPerPageOptions: [30, 50, 100],
    rowsPerPage: 30,
    viewColumns: false,
    downloadOptions: {
      filterOptions: {
        useDisplayedColumnsOnly: true,
        useDisplayedRowsOnly: true,
      },
    },
  };

  const getMuiTheme = () =>
    createTheme({
      components: {
        MUIDataTable: {
          styleOverrides: {
            root: {
              boxShadow: 'none',
              backgroundColor: '#FFF',
            },
          },
        },
        MUIDataTableHeadCell: {
          styleOverrides: {
            root: {
              backgroundColor: '#2F3542', // Keep background as you have it
              color: '#fff !important', // Ensure heading text is white
              border: 'none',
              fontWeight: '550 !important',
              '&:nth-last-of-type(1)': {
                borderTopRightRadius: '1rem',
                // borderBottomRightRadius: '0.2rem',
              },
            },
            sortActive: {
              color: '#fff !important', // Active sort icon color should also be white
            },
            sortAction: {
              '& .MuiTableSortLabel-icon': {
                color: '#fff !important', // Ensure sort icon stays white when active
              },
            },
            toolButton: {
              fontWeight: 'bold',
              fontSize: '0.675rem !important',
              paddingTop: '3px',
              paddingBottom: '3px',
            },
          },
        },
        MuiIconButton: {
          styleOverrides: {
            root: {
              padding: '2px 8px 2px 8px',
            },
          },
        },
        MuiTablePagination: {
          styleOverrides: {
            root: {
              backgroundColor: '#FFFFFF',
              height: '40px',
            },
            select: {
              backgroundColor: '#FFFFFF',
            },
            actions: {
              backgroundColor: '#FFFFFF',
            },
          },
        },
        MUIDataTableSelectCell: {
          styleOverrides: {
            headerCell: {
              border: 'none',
              borderTopLeftRadius: '0.2rem',
              borderBottomLeftRadius: '0.2rem',
              backgroundColor: '#2F3542',
              '& .MuiCheckbox-root': {
                color: '#fff',
                padding: '4px',
              },
            },
          },
        },
        MuiToolbar: {
          styleOverrides: {
            root: {
              minHeight: '30px!important',
              padding: '0px!important',
            },
          },
        },
        MuiCheckbox: {
          styleOverrides: {
            root: {
              padding: '4px',
            },
          },
        },
        MUIDataTableBodyRow: {
          styleOverrides: {
            root: {
              '&:nth-of-type(even)': {
                backgroundColor: '#EAF3FF',
              },
              '&:nth-of-type(odd)': {
                backgroundColor: '#FFF',
              },
              '& td': {
                textAlign: 'left', // Center align body text data
                border: 'none',
              },
              marginBottom: '10px',
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              color: '#fff',
              padding: '6px 0px !important',
              fontWeight: '550 !important',
              fontSize: '0.675rem !important',
              justifyContent: 'start',
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              padding: '2px 26px !important',
              fontSize: '0.675rem',
            },
          },
        },
        MUIDataTableBodyCell: {
          styleOverrides: {
            root: {
              fontSize: '0.675rem',
            },
          },
        },
        MuiPopover: {
          styleOverrides: {
            paper: {
              minWidth: '500px !important', // Increase the width of filter dropdown
              padding: '10px', // Add padding inside the dropdown
              borderRadius: '8px', // Optionally, round the corners
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)', // Add a subtle shadow for better visibility
              backgroundColor: '#fff', // Background color (you can change it)
            },
          },
        },
        MuiMenu: {
          styleOverrides: {
            paper: {
              minWidth: '200px  !important',
              maxHeight: '200px',
              overflowY: 'auto',
            },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              fontSize: '0.675rem !important',
              fontWeight: 500,
              color: '#333',
              padding: '8px 16px',
              '&:hover': {
                backgroundColor: '#F5F5F5',
              },
            },
          },
        },
        MuiSelect: {
          styleOverrides: {
            select: {
              fontSize: '0.675rem !important',
              padding: '6px 10px',
            },
          },
        },
      },
    });

  return (
    <LayoutContainer>
      <AdminHeader title='GPS UNITS' />
      <div className='page-content-wrapper scrollable'>
        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}
        >
          <Button
            variant='contained'
            sx={{
              backgroundColor: '#be210b',
              textTransform: 'none',
              fontSize: '0.675rem',
            }}
            onClick={() => navigate('/gpsUnitForm')}
          >
            ADD GPS UNIT
          </Button>
          <Button
            variant='contained'
            sx={{
              backgroundColor: '#be210b',
              textTransform: 'none',
              fontSize: '0.675rem',
            }}
            onClick={() => window.open('/map', '_blank')}
          >
            MAP TRACKING
          </Button>
          {/* <Button
            variant='contained'
            sx={{
              backgroundColor: '#be210b',
              textTransform: 'none',
              fontSize: '0.675rem',
            }}
            onClick={() => window.open('/incidents', '_blank')}
          >
            INCIDENT TRACKING
          </Button> */}
          <Button
            variant='contained'
            sx={{
              backgroundColor: '#be210b',
              textTransform: 'none',
              fontSize: '0.675rem',
            }}
            onClick={() => window.open('/map-history', '_blank')}
          >
            MAP HISTORY
          </Button>
          {(isCMCPermission || isCMCRoles) && (
            <Button
              variant='contained'
              sx={{
                backgroundColor: '#be210b',
                textTransform: 'none',
                fontSize: '0.675rem',
              }}
              onClick={() => window.open('/control-room-chat', '_blank')}
            >
              CHAT
            </Button>
          )}
        </Box>
        <ThemeProvider theme={getMuiTheme()}>
          <MUIDataTable data={data} columns={columns} options={options} />
        </ThemeProvider>
        {/* <Table className='table'> */}
        {/* <TableHead className='tableHead'>
            <TableRow>
              <TableCell>Icons</TableCell>
              <TableCell>Unit Name</TableCell>
              <TableCell>Unit Type</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody className='tableBody'>
            {Object.keys(units).map((unitType, index) => (
              <React.Fragment key={unitType}>
               
                {units[unitType].map((unit, idx) => (
                  <TableRow
                    key={unit.id}
                    sx={{
                      backgroundColor:
                        (index + idx) % 2 === 0 ? '#fff' : '#eaf3ff',
                    }}
                  >
                    <TableCell>
                      <img
                        src={`${process.env.PUBLIC_URL}/icons/${unit.icon}`}
                        alt='icon'
                        style={{ height: '28px', marginRight: '10px' }}
                      />
                    </TableCell>
                    <TableCell>{unit.name}</TableCell>
                    <TableCell>{unit.unit_type}</TableCell>
                    <TableCell>{unit.user_name || '-'}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => navigate(`/gpsUnitForm/${unit.id}`)}
                      >
                        <EditIcon sx={{ color: '#be210b' }} />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(unit?.id)}>
                        <DeleteIcon sx={{ color: '#be210b' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody> */}
        {/* <TableBody className='tableBody'>
            {units?.map((unit, index) => (
              <TableRow
                key={unit.id}
                sx={{
                  backgroundColor: index % 2 === 0 ? '#fff' : '#eaf3ff',
                
                }}
              >
                <TableCell>
                  <img
                    src={`${process.env.PUBLIC_URL}/icons/${unit?.icon}`}
                    alt='icon'
                    style={{ h: '40px', height: '28px', marginRight: '10px' }}
                  />
                </TableCell>
                <TableCell>{unit.name}</TableCell>
                <TableCell>{unit.unit_type}</TableCell>
                <TableCell>{unit.user_name}</TableCell>
              
                <TableCell>
                  <IconButton
                    onClick={() => navigate(`/gpsUnitForm/${unit.id}`)}
                  >
                    <EditIcon sx={{ color: '#be210b' }} />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(unit?.id)}>
                    <DeleteIcon sx={{ color: '#be210b' }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody> */}
        {/* </Table> */}
      </div>
      <DeleteModal
        open={openDeleteModal}
        handleDailogClose={() => setOpenDeleteModal(false)}
        handleDelete={() => {
          handleDelete(rowsToDelete);
        }}
      />
    </LayoutContainer>
  );
};

export default GpsUnitList;
